import { useState, useEffect, useRef, startTransition } from "react";
import type { CheckoutSession } from "@/types";

interface UseWalletCardSyncArgs {
  session?: CheckoutSession;
}

/**
 * Auto-selects the wallet balance when the customer has a positive balance.
 *
 * The wallet toggle defaults to ON the first time a positive balance is observed
 * so available store credit is applied by default. If the customer manually turns
 * the wallet off we do not re-enable it (tracked via `walletAutoSelectedRef`).
 *
 * We intentionally do NOT clear the selected card / Apple Pay when the wallet can
 * cover the full down payment. Keeping the selection lets the payment-method
 * chooser stay rendered (faded and non-interactive) instead of collapsing to an
 * empty box. Which source actually charges the customer is decided at submit time
 * via `PaymentMethodUtils.shouldPayWithWalletOnly`, so no state-level mutual
 * exclusion is required here.
 */
export function useWalletCardSync({ session }: UseWalletCardSyncArgs) {
  const [isWalletSelected, setIsWalletSelected] = useState(false);
  const walletAutoSelectedRef = useRef(false);

  useEffect(() => {
    const hasWallet =
      session?.paymentDetails?.availableWalletBalance &&
      session.paymentDetails.availableWalletBalance.amount > 0;

    if (hasWallet && !walletAutoSelectedRef.current) {
      startTransition(() => {
        setIsWalletSelected(true);
      });
      walletAutoSelectedRef.current = true;
    } else if (!hasWallet) {
      walletAutoSelectedRef.current = false;
    }
  }, [session?.paymentDetails?.availableWalletBalance]);

  return {
    isWalletSelected,
    setIsWalletSelected,
  };
}
